/* eslint-disable react/no-unescaped-entities */
import matchSorter from 'match-sorter';
import { Component } from 'react';
import { findDOMNode } from 'react-dom';
import { BACKSPACE_KEY, DOWN_KEY, ENTER_KEY, ESCAPE_KEY, LEFT_KEY, RIGHT_KEY, TAB_KEY, UP_KEY } from '../../utils/keys';
import { ClickAwayListener } from '../ClickAwayListener/ClickAwayListener';
import { FormItem, ValidationState } from '../Form/FormItem';
import { Group } from '../Group/Group';
import { Icon } from '../Icon/Icon';
import {
  FaceElement,
  InputElement,
  MenuButtonElement,
  MenuItemElement,
  MenuListElement,
  MultiSelectElement,
  NoItemsElement,
  TagListElement,
  TagWrapperElement,
} from './MultiSelect.styles';
import { Tag } from './Tag';

export interface IMultiSelectItem {
  id: string;
  label: string;
}

export interface IProps {
  /** MultiSelect label */
  label?: string;
  /** List of items where each item has a string-based `id` and a descriptive `label` */
  items?: IMultiSelectItem[];
  /** List of selected items */
  selectedItems?: IMultiSelectItem[];
  /** Event invoked whenever the items change */
  onChange?: (selectedItems: IMultiSelectItem[]) => void;
  /** Whether the input is initially focused or not.  Defaults to false */
  autoFocus?: boolean;
  /** The tab index of the multi select input.  Defaults to 0. */
  tabIndex?: number;
  /** The ability to add new items */
  creatable?: boolean;
  /** Input validation state (complete, warning)  */
  validationState?: ValidationState;
  /** Input validation message  */
  validationMessage?: string;
  /** A hint can be passed as HTML to explain what the MultiSelect is used for */
  hint?: any;
  /** Optional tooltip heading.  Defaults to undefined. */
  tooltipHeading?: string;
  /** Optional tooltip description.  Defaults to undefined. */
  tooltipDescription?: string;
  /** Optional tooltip width. Defaults to auto */
  tooltipWidth?: string;
  /** Height of the menu in pixels. Defaults to 250. */
  menuHeight?: number;
}

export interface IState {
  showMenu: boolean;
  creatableItemDuplication: boolean;
  inputValue: string;
  highlightedIndex: number;
  menuListLeftPosition: number;
  menuListTopPosition: number;
  horizontalArrowKeyCount: number;
}

/** @deprecated Do not use. Use `SelectMulti` instead */
export class MultiSelect extends Component<IProps, IState> {
  public static defaultProps: Pick<IProps, 'items' | 'selectedItems' | 'menuHeight'> = {
    items: [],
    selectedItems: [],
    menuHeight: 200,
  };
  public state: IState = {
    highlightedIndex: -1,
    inputValue: '',
    showMenu: false,
    creatableItemDuplication: false,
    menuListLeftPosition: 0,
    menuListTopPosition: 0,
    horizontalArrowKeyCount: this.props.selectedItems.length + 1,
  };
  private inputRef: any;
  private menuItemRefs: any[] = [];

  public componentDidMount() {
    if (import.meta.env.VITE_ENV === 'poc') {
      // eslint-disable-next-line no-console
      console.warn("'MultiSelect' component has been deprecated. Please use 'SelectMulti'");
    }
  }

  public render() {
    const { selectedItems } = this.props;
    const { inputValue } = this.state;
    const filteredItems = this.getFilteredItems();
    const showMenu = this.shouldShowMenu();
    const allItemsSelected = selectedItems && this.props.items && this.getAvailableItems().length === 0;
    const inputItem = this.constructItem(inputValue);
    return (
      <FormItem
        label={this.props.label}
        validationMessage={this.props.validationMessage}
        validationState={this.props.validationState}
        hint={this.props.hint}
        tooltipHeading={this.props.tooltipHeading}
        tooltipDescription={this.props.tooltipDescription}
        tooltipWidth={this.props.tooltipWidth}
      >
        <MultiSelectElement
          onKeyDown={this.handleKeyDown}
          onClick={this.handleMenuButtonClicked}
          data-testid="MultiSelect"
        >
          <FaceElement>
            <TagListElement data-testid="TagList">
              {selectedItems.map((item, index) => (
                <TagWrapperElement key={index}>
                  <Tag
                    onClick={this.handleBadgeClicked.bind(this, item)}
                    active={this.state.horizontalArrowKeyCount === ++index}
                  >
                    {item.label}
                  </Tag>
                </TagWrapperElement>
              ))}
              {(!allItemsSelected || this.props.creatable) && (
                <InputElement
                  ref={this.refInput}
                  value={inputValue}
                  onChange={this.handleInputChanged}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus={this.props.autoFocus}
                  placeholder="Type here..."
                  data-testid="InputElement"
                  tabIndex={this.props.tabIndex || 0}
                  onClick={this.handleInputClick}
                  onKeyUp={this.handleInputKeyUp}
                  className={selectedItems.length >= this.state.horizontalArrowKeyCount ? 'inactive' : ''}
                />
              )}
            </TagListElement>

            <Group>
              <MenuButtonElement
                onClick={this.handleClearAllButton}
                onKeyDown={this.handleClearAllButton}
                data-testid="ClearAllButton"
                type="button"
                tabIndex={0}
              >
                <Icon icon="times" size="large" />
              </MenuButtonElement>
              {!allItemsSelected && this.props.items.length > 1 && (
                <MenuButtonElement
                  onClick={this.handleMenuButtonClicked}
                  data-testid="MenuButton"
                  type="button"
                  tabIndex={0}
                >
                  <Icon icon="angle-down" size="large" />
                </MenuButtonElement>
              )}
            </Group>
          </FaceElement>
          {showMenu && (
            <ClickAwayListener onClickAway={this.handleClickAway}>
              <MenuListElement
                data-testid="MenuList"
                menuHeight={this.props.menuHeight}
                numItems={filteredItems.length}
              >
                {filteredItems.map((item, index) => (
                  <MenuItemElement
                    tabIndex={-1}
                    key={index}
                    ref={el => (this.menuItemRefs[index] = el)}
                    isHighlighted={index === this.state.highlightedIndex}
                    onClick={() => this.handleMenuItemClicked(item)}
                    onMouseEnter={() => this.handleMouseEnteredItem(index)}
                  >
                    {item.label}
                  </MenuItemElement>
                ))}
                {inputValue.trim().length > 0 && this.props.creatable && !this.state.creatableItemDuplication && (
                  <MenuItemElement
                    isHighlighted={filteredItems.length === this.state.highlightedIndex}
                    key={filteredItems.length + 1}
                    onClick={this.handleMenuItemClicked.bind(this, inputItem)}
                    onMouseEnter={this.handleMouseEnteredItem.bind(this, inputItem)}
                  >
                    Create "{inputItem.label.trim()}"
                  </MenuItemElement>
                )}
                {!this.props.creatable && filteredItems.length === 0 && <NoItemsElement>No options</NoItemsElement>}
              </MenuListElement>
            </ClickAwayListener>
          )}
        </MultiSelectElement>
      </FormItem>
    );
  }

  public focusMenuItem = highlightedIndex => {
    // eslint-disable-next-line react/no-find-dom-node
    const el: HTMLElement = findDOMNode(this.menuItemRefs[highlightedIndex]) as HTMLElement;

    if (el != null) {
      el.focus();
      // This focuses the input directly after the menu item is focused, which
      // works just fine. We're only focusing the menu item to ensure scrolling
      // works with keyboard nav after all.
      this.focus();
    }
  };

  private shouldShowMenu() {
    return (
      this.state.showMenu ||
      (this.state.inputValue.length > 0 && (!this.state.creatableItemDuplication || this.getFilteredItems().length > 0))
    );
  }

  private handleKeyDown = e => {
    const filteredItems = this.getFilteredItems();
    const availableItems = this.getAvailableItems();
    switch (e.keyCode) {
      case UP_KEY: {
        e.stopPropagation();
        e.preventDefault();
        const highlightedIndex = this.state.highlightedIndex - 1;
        if (highlightedIndex >= 0) {
          this.setState({ showMenu: true, highlightedIndex });
        }
        this.focusMenuItem(highlightedIndex);
        break;
      }
      case DOWN_KEY: {
        e.stopPropagation();
        e.preventDefault();
        const highlightedIndex = this.state.highlightedIndex + 1;
        if (
          highlightedIndex < filteredItems.length ||
          (this.props.creatable && highlightedIndex < availableItems.length)
        ) {
          this.setState({
            showMenu: true,
            highlightedIndex,
          });
        }
        this.focusMenuItem(highlightedIndex);
        break;
      }
      case TAB_KEY: {
        if (this.shouldShowMenu() === true && this.state.highlightedIndex !== -1) {
          e.preventDefault();
          this.handleEnterKeyPressed(filteredItems);
          this.focus();
        } else {
          // If menu is not displayed, let TAB key event pass through so it
          // focuses the ClearButton.
        }
        break;
      }
      case ENTER_KEY: {
        e.preventDefault();
        this.handleEnterKeyPressed(filteredItems);
        break;
      }
      case BACKSPACE_KEY: {
        if (this.state.horizontalArrowKeyCount <= this.props.selectedItems.length) {
          const selectedItems = this.props.selectedItems.filter((_, i) => {
            return i + 1 !== this.state.horizontalArrowKeyCount;
          });
          this.notifyChange(selectedItems);
        }
        break;
      }
      case RIGHT_KEY: {
        if (this.state.horizontalArrowKeyCount <= this.props.selectedItems.length) {
          e.preventDefault();
          this.setState(prevState => ({
            horizontalArrowKeyCount: prevState.horizontalArrowKeyCount + 1,
          }));
        }
        break;
      }
      case LEFT_KEY: {
        if (e.target.selectionStart === 0 && this.state.horizontalArrowKeyCount > 1) {
          this.setState({
            horizontalArrowKeyCount: this.state.horizontalArrowKeyCount - 1,
          });
        }
        break;
      }
      // eslint-disable-next-line no-duplicate-case
      case TAB_KEY:
      case ESCAPE_KEY: {
        this.setState({
          showMenu: false,
          inputValue: '',
          highlightedIndex: -1,
        });
        break;
      }
    }
  };

  private handleEnterKeyPressed(filteredItems) {
    if (this.state.highlightedIndex !== -1 && filteredItems.length > this.state.highlightedIndex) {
      this.addSelectedItem(filteredItems[this.state.highlightedIndex]);
    } else if (
      this.props.creatable &&
      this.state.inputValue.trim().length > 1 &&
      !this.props.selectedItems.some(item => item.label === this.state.inputValue) &&
      this.state.inputValue !== ''
    ) {
      this.addSelectedItem(this.constructItem(this.state.inputValue));
    } else if (this.state.horizontalArrowKeyCount <= this.props.selectedItems.length) {
      const selectedItems = this.props.selectedItems.filter((_, i) => {
        return i + 1 !== this.state.horizontalArrowKeyCount;
      });
      this.notifyChange(selectedItems);
    }
  }

  private handleClickAway = () => {
    this.setState({ showMenu: false, inputValue: '', highlightedIndex: -1 });
  };

  private handleInputChanged = e => {
    if (this.props.selectedItems.some(item => item.label === e.target.value)) {
      this.setState({ creatableItemDuplication: true });
    } else {
      this.setState({ creatableItemDuplication: false });
    }
    this.setState({
      inputValue: e.target.value,
      highlightedIndex: e.target.value.length > 0 ? 0 : -1,
    });
  };

  private handleInputClick = () => {
    this.setState({
      horizontalArrowKeyCount: this.props.selectedItems.length + 1,
    });
  };

  private handleInputKeyUp = e => {
    if (e.keyCode !== LEFT_KEY && e.keyCode !== RIGHT_KEY && e.keyCode !== BACKSPACE_KEY) {
      this.setState({
        horizontalArrowKeyCount: this.props.selectedItems.length + 1,
      });
    }
  };

  private handleMenuItemClicked = item => {
    this.addSelectedItem(item);
  };

  private addSelectedItem(item) {
    const selectedItems = Array.from(new Set([...this.props.selectedItems, item]));
    this.setState({
      inputValue: '',
      showMenu: false,
      highlightedIndex: -1,
      horizontalArrowKeyCount: selectedItems.length + 1,
    });
    this.notifyChange(selectedItems);
    this.focus();
  }

  private handleMouseEnteredItem = highlightedIndex => {
    this.setState({ highlightedIndex });
  };

  private handleBadgeClicked = item => {
    const selectedItems = this.props.selectedItems.filter(i => i.id !== item.id);
    this.setState({
      horizontalArrowKeyCount: selectedItems.length + 1,
    });
    this.notifyChange(selectedItems);
    this.focus();
  };

  private handleMenuButtonClicked = () => {
    this.setState({
      showMenu: !this.state.showMenu,
    });
  };

  private handleClearAllButton = e => {
    if (e.keyCode !== undefined) {
      return e.keyCode === ENTER_KEY && this.clearAllSelectedItems();
    } else {
      this.clearAllSelectedItems();
    }
  };

  private notifyChange = selectedItems => {
    if (this.props.onChange) {
      this.props.onChange(selectedItems);
    }
  };

  private clearAllSelectedItems = () => {
    const selectedItems = [];
    this.setState({ showMenu: false, inputValue: '', highlightedIndex: -1 });
    this.notifyChange(selectedItems);
    this.focus();
  };

  private getFilteredItems = () => {
    if (this.props.items) {
      let availableItems = this.getAvailableItems();
      availableItems = matchSorter(availableItems, this.state.inputValue, {
        keys: ['label'],
        threshold: matchSorter.rankings.CONTAINS,
      });
      return availableItems;
    } else {
      return [];
    }
  };

  private getAvailableItems = () => {
    return this.props.items.filter(
      (i1: IMultiSelectItem) => this.props.selectedItems.find(i2 => i2.id === i1.id) == null,
    );
  };

  private focus() {
    if (this.inputRef) {
      this.inputRef.focus();
    }
  }

  private constructItem(value) {
    return {
      label: value,
      id: value.replace(/\s+/g, '-').toLowerCase(),
    };
  }

  private refInput = (el: any) => (this.inputRef = el);
}
